import React, { Component, type ReactNode, type RefObject } from 'react';

import classNames from 'classnames';
import _throttle from 'lodash-es/throttle';

import type { ColumnWidthDefinition } from '@iconik-io/shared-lib/src/constants/search';
import type {
  SortObject,
  SortOrder,
} from '@iconik-io/shared-lib/src/utils/sort/types';
import { Icon, type IconName } from '@iconik-io/uad';

import { ORDER_ASC, ORDER_DESC } from '../../../constants/data';
import ColumnDragSource from '../column_drag_source/column_drag_source';
import ColumnDropMark from '../column_drop_mark/column_drop_mark';
import ColumnDropTarget from '../column_drop_target/column_drop_target';
import type { Column } from '../types';
import { type ColumnState, redistributeWidth, resizeColumns } from '../utils';

import mixins from '../../../styles/core/mixins.module.css';
import tableStyles from '../table.module.css';
import styles from './custom_head.module.css';

type Props<T> = {
  renderColumns: Column<T>[];
  sorting?: SortObject;
  onSort?: (name: string, order: SortOrder | undefined) => void;
  onColumnsResized?: (columns: ColumnWidthDefinition[]) => void;
  onColumnsUpdated?: (columns: ColumnWidthDefinition[]) => void;
  onColumnDragStart?: () => void;
  onColumnDragEnd?: () => void;
  onColumnReordered?: (fromIndex: number, toIndex: number) => void;
  draggableColumns?: boolean;
  width: number | null;
  // Collapsed flag hides the header visually but the header still able to
  // control widths of columns.
  collapsed?: boolean;
  titleWidth?: number;
  isTitleAutoResize?: boolean;
};

type State = {
  resizingColumn: number | null;
  draggingColumn: number | null;
};

class CustomHead<T> extends Component<Props<T>, State> {
  rootRef = React.createRef<HTMLTableSectionElement>();

  columnRefs: RefObject<HTMLTableHeaderCellElement | null>[] = [];

  markRefs: RefObject<HTMLDivElement | null>[] = [];

  columns: ColumnState[] = [];

  hoverMarkIndex: number | null = null;

  state: State = {
    resizingColumn: null,
    draggingColumn: null,
  };

  componentDidMount(): void {
    this.updateColumns();
  }

  componentWillUnmount(): void {
    this.columnRefs = [];
    this.markRefs = [];
    this.columns = [];
  }

  componentDidUpdate(prevProps: Readonly<Props<T>>): void {
    const updatedColumns = this.props.renderColumns.filter(
      (item) =>
        !prevProps.renderColumns.some((newItem) => item.name === newItem.name),
    );

    if (
      updatedColumns.length > 0 ||
      this.props.titleWidth !== prevProps.titleWidth
    ) {
      this.updateColumns();
    }

    if (this.props.width !== null && this.props.width !== prevProps.width) {
      this.columns = redistributeWidth(this.columns, this.props.width);
      this.updateSizes();

      if (prevProps.width !== null) {
        // skip init callback handler
        this.handleColumnsResized();
      }
    }
  }

  handleColumnsResized = (): void => {
    const { onColumnsResized } = this.props;
    if (onColumnsResized) {
      onColumnsResized(this.columns);
    }
  };

  updateColumns = (): void => {
    const { onColumnsUpdated, titleWidth, isTitleAutoResize } = this.props;
    this.columns = this.columnRefs.map((columnRef, index) => {
      if (!columnRef.current) {
        return {
          current: 0,
        };
      }
      const { width: widthDefinition = {} } = this.props.renderColumns[index];
      const current =
        widthDefinition.current ||
        columnRef.current.getBoundingClientRect().width;
      const min = widthDefinition.fixed || widthDefinition.min || 100;
      const max = widthDefinition.fixed || widthDefinition.max;

      if (
        titleWidth !== undefined &&
        isTitleAutoResize &&
        widthDefinition.autoResize
      ) {
        return {
          min,
          current: current > titleWidth ? current : titleWidth,
          autoResize: true,
        };
      }

      return {
        current: Math.max(min, Math.min(current, max || Number.MAX_VALUE)),
        min,
        max,
      };
    });

    if (this.props.width) {
      this.columns = redistributeWidth(this.columns, this.props.width);

      if (onColumnsUpdated) {
        onColumnsUpdated(this.columns);
      }
    }

    this.updateSizes();
  };

  updateSizes = (): void => {
    if (this.props.width && this.props.titleWidth !== undefined) {
      this.columns = redistributeWidth(this.columns, this.props.width);
    }
    this.columnRefs.forEach((ref, index) => {
      if (!ref.current) {
        return;
      }
      ref.current.style.width = `${this.columns[index].current}px`;
    });
  };

  handleResizerMouseMove = _throttle((event) => {
    const { resizingColumn } = this.state;
    if (resizingColumn === null) {
      return;
    }
    const columnRef = this.columnRefs[resizingColumn];
    // The current component can be unmounted at this point, see componentWillUnmount
    if (!columnRef?.current) {
      return;
    }
    const width =
      event.clientX - columnRef.current.getBoundingClientRect().left - 3;
    this.columns = resizeColumns(this.columns, resizingColumn, width);
    this.updateSizes();
  }, 33);

  handleResizerMouseDown = (event: React.MouseEvent<HTMLDivElement>): void => {
    const resizingColumn = Number(event.currentTarget.dataset.index) - 1;
    this.setState((state) => ({
      resizingColumn: state.draggingColumn === null ? resizingColumn : null,
    }));
    document.addEventListener('mousemove', this.handleResizerMouseMove);
    document.addEventListener('mouseup', this.handleResizerMouseUp);
  };

  handleResizerMouseUp = (): void => {
    document.removeEventListener('mousemove', this.handleResizerMouseMove);
    document.removeEventListener('mouseup', this.handleResizerMouseUp);
    this.setState({ resizingColumn: null });
    this.handleColumnsResized();
  };

  handleBeginDrag = ({ index }: { index: number }): void => {
    const { onColumnDragStart } = this.props;
    if (onColumnDragStart) {
      onColumnDragStart();
    }
    this.setState((state) => ({
      draggingColumn: state.resizingColumn === null ? index : null,
    }));
  };

  handleEndDrag = (): void => {
    const { onColumnReordered, onColumnDragEnd } = this.props;
    if (onColumnReordered && this.hoverMarkIndex !== null) {
      const { draggingColumn } = this.state;
      if (draggingColumn !== null) {
        onColumnReordered(draggingColumn, this.hoverMarkIndex);
      }
    }
    this.hoverMarkIndex = null;
    this.setState({ draggingColumn: null });
    this.updateHoverMarks();

    if (onColumnDragEnd) {
      onColumnDragEnd();
    }
  };

  handleColumnDropTargetHover = _throttle((index, side) => {
    let hoverMarkIndex = side === 'right' ? index + 1 : index;
    const { draggingColumn } = this.state;
    if (draggingColumn === null || draggingColumn === index) {
      hoverMarkIndex = null;
    }

    if (draggingColumn === index + 1 && side === 'right') {
      hoverMarkIndex -= 1;
    }
    if (draggingColumn === index - 1 && side === 'left') {
      hoverMarkIndex += 1;
    }

    this.hoverMarkIndex = hoverMarkIndex;
    this.updateHoverMarks();
  }, 33);

  updateHoverMarks = (): void => {
    this.markRefs.forEach((ref, i) => {
      if (!ref.current) {
        return;
      }
      if (this.hoverMarkIndex === i) {
        ref.current.style.opacity = '1';
      } else {
        ref.current.style.opacity = '0';
      }
    });
  };

  render(): ReactNode {
    const { onSort, renderColumns, sorting, draggableColumns, collapsed } =
      this.props;
    const { resizingColumn, draggingColumn } = this.state;

    this.columnRefs = Array.from({ length: renderColumns.length }, () =>
      React.createRef(),
    );
    this.markRefs = Array.from({ length: renderColumns.length + 1 }, () =>
      React.createRef(),
    );

    return (
      <thead
        className={classNames({
          [styles.head_resizing]: resizingColumn,
          [styles.draggable_columns]: draggableColumns,
        })}
        ref={this.rootRef}
      >
        <tr>
          {renderColumns.map(
            (
              {
                name,
                type,
                label,
                hideLabel,
                style,
                sortable,
                originFieldName,
              },
              index,
            ) => {
              const sortByName = originFieldName ?? name;
              let iconName: IconName = 'sort';
              if (sortable && sorting) {
                if (sorting[sortByName] === ORDER_ASC) {
                  iconName = 'arrow_small_n';
                } else if (sorting[sortByName] === ORDER_DESC) {
                  iconName = 'arrow_small_s';
                }
              }
              const headingLabel = !hideLabel ? label || name : '';
              const content = sortable ? (
                <span
                  className={tableStyles.sort_header}
                  onClick={(): void => {
                    if (onSort && sorting) {
                      onSort(sortByName, sorting[sortByName]);
                    }
                  }}
                >
                  {headingLabel}
                  <Icon
                    name={iconName}
                    size="xs"
                    className={classNames(
                      tableStyles.sort_icon,
                      mixins.text_align_icon,
                    )}
                  />
                </span>
              ) : (
                headingLabel
              );
              const labelIcon = name.startsWith('metadata.') && (
                <Icon
                  name="Svg2022_uad_metadata"
                  className={classNames(
                    tableStyles.label_icon,
                    mixins.text_align_icon,
                  )}
                />
              );
              const firstColumn = index === 0;
              const lastColumn = index === renderColumns.length - 1;

              const thProps = {
                ref: this.columnRefs[index],
                className: classNames(type, tableStyles.th),
                style,
                role: 'columnheader',
              };

              if (collapsed) {
                return <th key={name} {...thProps} />;
              }

              return (
                <th key={name} {...thProps}>
                  <div className={tableStyles.clipped_shadow} />
                  <ColumnDropTarget
                    index={index}
                    isActive={draggingColumn !== null}
                    side="left"
                    onHover={this.handleColumnDropTargetHover}
                  />
                  <ColumnDropMark ref={this.markRefs[index]} side="left" />
                  <div className={tableStyles.label_wrapper}>
                    {!firstColumn && (
                      <div
                        className={classNames(
                          styles.label_resizer,
                          mixins.hitbox_expand,
                          {
                            [styles.dragging]:
                              resizingColumn !== null &&
                              index === resizingColumn + 1,
                            [styles.inactive]: draggingColumn !== null,
                          },
                        )}
                        data-index={index}
                        onMouseDown={this.handleResizerMouseDown}
                      />
                    )}
                    <ColumnDragSource
                      className={classNames(tableStyles.label, styles.label, {
                        [styles.dragging]: draggingColumn === index,
                      })}
                      onBeginDrag={this.handleBeginDrag}
                      onEndDrag={this.handleEndDrag}
                      index={index}
                      title={headingLabel}
                    >
                      {labelIcon}
                      {content}
                    </ColumnDragSource>
                  </div>
                  <ColumnDropTarget
                    index={index}
                    isActive={draggingColumn !== null}
                    side="right"
                    onHover={this.handleColumnDropTargetHover}
                  />
                  {lastColumn && (
                    <ColumnDropMark
                      ref={this.markRefs[index + 1]}
                      side="right"
                    />
                  )}
                </th>
              );
            },
          )}
        </tr>
      </thead>
    );
  }
}

export default CustomHead;
